import Collection from '@repo/utils/models/Collection';
import Cadence from '@repo/utils/models/Cadence';
import type { ID, ReduxAction } from '@repo/utils/types';
import Occasion from '@repo/utils/helpers/Occasion';
import AppState from '../app-state';

// collection of cadences is keyed by contactID
export interface ReconnectState {
  collection: Collection<Cadence>;
}

const initialReconnectState = {
  collection: new Collection<Cadence>(),
};

export const selectContactReconnectCadence = (contactID: ID) => (state: AppState) =>
  state.data.reconnect.collection.items.get(String(contactID), new Cadence({ type: 'auto' }));

export const selectContactReconnectCadenceType = (contactID: ID) => (state: AppState) => {
  const reconnectCadence = selectContactReconnectCadence(contactID)(state);
  return reconnectCadence.updated ? reconnectCadence.type : 'auto';
};

export function reconnectReducer(
  state: ReconnectState = initialReconnectState,
  { type, payload }: ReduxAction,
) {
  const contactID = String(payload?.contactID);

  switch (type) {
    case 'FETCH_RECONNECT_CADENCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactID,
            new Cadence({ isFetching: true }),
            (cadence) => cadence.copyWith({ isFetching: true, error: false }),
          ),
        }),
      };

    case 'FETCH_RECONNECT_CADENCE_SUCCEEDED':
    case 'CREATE_RECONNECT_CADENCE_SUCCEEDED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (cadence) =>
            cadence.copyWith({
              ...payload.cadence,
              isFetching: false,
              updated: new Occasion(),
            }),
          ),
        }),
      };

    case 'CREATE_RECONNECT_CADENCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactID,
            new Cadence({ isFetching: true }),
            (cadence) => cadence.copyWith({ ...payload.cadence, isFetching: true, error: false }),
          ),
        }),
      };

    case 'DELETE_RECONNECT_CADENCE_REQUESTED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(
            contactID,
            new Cadence({ type: 'none' }),
            (cadence) => cadence.copyWith({ type: 'none' }),
          ),
        }),
      };

    case 'FETCH_RECONNECT_CADENCE_FAILED':
    case 'CREATE_RECONNECT_CADENCE_FAILED':
      return {
        collection: state.collection.copyWith({
          items: state.collection.items.update(contactID, (cadence) =>
            cadence.copyWith({ error: true, isFetching: false }),
          ),
        }),
      };

    // Action types not implemented:
    case 'DELETE_RECONNECT_CADENCE_SUCCEEDED':
    case 'DELETE_RECONNECT_CADENCE_FAILED':
    default:
      return state;
  }
}
